import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Link,
  CircularProgress
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../utils/api';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Clear error when user starts typing
  const handleChange = (e) => {
    setEmail(e.target.value);
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loading) return;
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Trim email to prevent whitespace issues
      const trimmedEmail = email.trim();
      
      if (!trimmedEmail) {
        throw new Error('Email is required');
      }

      // The correct API path - the api utility already includes the base URL with '/api'
      // So we need to use '/api/auth/forgot-password' here
      await api.post('/api/auth/forgot-password', { email: trimmedEmail });
      setSuccess('Password reset instructions have been sent to your email');
    } catch (error) {
      setError(
        error.response?.data?.message || 
        error.message || 
        'Failed to process request. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'secondary.main', // Lavender background
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4
    }}>
      {/* Logo */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 4
        }}
      >
        <img src={`${process.env.PUBLIC_URL}/img/logo.svg`} alt="Idotoo Logo" style={{ height: '60px' }} />
      </Box>

      {/* Forgot Password Card */}
      <Paper 
        elevation={0}
        sx={{ 
          backgroundColor: 'background.paper',
          maxWidth: '400px',
          width: '100%',
          mx: 2,
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}
      >
        <Box sx={{ p: 4 }}>
          <Typography 
            variant="h5" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 500,
              color: 'text.primary',
              mb: 2
            }}
          >
            Forgot Password
          </Typography>
          <Typography 
            variant="body2" 
            align="center" 
            sx={{ 
              mb: 3,
              color: 'text.secondary'
            }}
          >
            Enter your email address and we'll send you instructions to reset your password
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              placeholder="Email"
              type="email"
              value={email}
              onChange={handleChange}
              margin="normal"
              required
              variant="outlined"
              autoFocus
              sx={{ 
                mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff'
                  }
              }}
            />
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ 
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                border: '1px solid #000'
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>
          </Box>
          
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Link 
              component={RouterLink} 
              to="/login" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.875rem',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Back to Login
            </Link>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default ForgotPassword; 